import * as _ from "lodash";
import * as angular from "angular";
import * as Moment from "moment";

export class BVUtils {
    static GetDate(val: any, allowNullDate: boolean = false) {
		let result: Date = null;
		if (_.isDate(val)) {
			result = new Date(val.getTime());
        } else if (_.isString(val)) {
            result = new Date(val);

            if (BVUtils.IsInvalidDate(result)) {
                //IE has a more limited set of date parsers than other platforms.  I am going to use Moment
                //to try to parse this date so that it doesn't use an invalid format
                //Unfortunately, moment falls back to using the browser date format to turn strings
                //into dates.  We can supply a format here to match what we use, but we would need 
                //a standard format throughout the platform.  And we haven't defined that yet.  
                //This format matches the toLocaleString format.  
                let m = Moment(val, 'M/D/YYYY H:m:s a', false);
                if (m.isValid()) {
                    result = m.toDate();
                }
            }

		}
		else if (!allowNullDate) 	{
			result = new Date(val);     
		} 
		return result;
	}

	static GetStartOfDay(val: Date) {
		let result: Date;
		if (angular.isUndefined(val)) {
			return null;
		} else {
			result = new Date(val.setHours(0, 0, 0));
		}
		return result;
	}

	static IsInvalidDate(val: any) {
		return val instanceof Date && isNaN(<any>val);
	}

	static GetEndOfDay(val: Date) {
		let result: Date;
		if (angular.isUndefined(val)) {
			return null;
		} else {
			result = new Date(val.setHours(23, 59, 59, 999));
		}
		return result;
	}

	static turnIntoNumber(val: string | number) {
		let result: number;
		if (typeof val === "string") {
			result = parseFloat(val);
		} else {
			result = val;
		}
		return result;
	}

	static GetDateKey(startDate: Date | string, endDate: Date | string) {
		let k1: string = "";
		let k2: string = "";

		if (_.isDate(startDate)) {
			k1 = startDate.getTime().toString();
		} else if (_.isString(startDate)) {
			k1 = startDate || "";
		}

		if (_.isDate(endDate)) {
			k2 = endDate.getTime().toString();
		} else if (_.isString(endDate)) {
			k2 = endDate || "";
		}

		return k1 + ":" + k2;
	}

	static StringCompare(string1: string, string2: string) {
		let result: number = 0;
		if (string1 > string2) {
			result = 1;
		} else if (string1 < string2) {
			result = -1;
		}
		return result;
	}

	//Its better to use feature detection whenever possible.  We have Modernizr installed and it can be used.
	//If the browser type is absolutely necessary this can be used. 
	//Found on https://stackoverflow.com/questions/5916900/how-can-you-detect-the-version-of-a-browser
    //updated to fix Edge
	static GetBrowser() {
        var ua = navigator.userAgent, tem,
            M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return { name: 'IE', version: (tem[1] || '') };
        }
        if (M[1] === 'Chrome') {
            tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
            if (tem != null) {
                return { name: tem[1].replace('OPR', 'Opera'), version: tem[2] };
            }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
        if ((tem = ua.match(/version\/(\d+)/i)) != null) {
            M.splice(1, 1, tem[1]);
        }
        return { name: M[0], version: M[1] };
	}

	static IsPromise<T>(item:T | angular.IPromise<T>): item is angular.IPromise<T> {
		return (<angular.IPromise<T>>item).then !== undefined;
	}

	//In Investment Accelerator, we use this function to get the stacked area charts to behave better - they only 'stack' 
	//on x-coordinates shared between series. This function gets called for each series with the 'master' list of timestamps 
	//and each series' points list, to fill out the missing timestamps with the 'last good value' off the series.
	//This works so far, but only because all the places we use it have series with distinct, non-overlapping timeranges.
	//We're going to need a better linear interpolation function if we ever want to stack projects with overlapping timeranges.
    public static FillMissingTimestamps(points: Highcharts.DataPoint[], timestamps: number[]) {
        //Fill in the missing timestamps
        if (points.length > 0) {
            var idx = 0;
            var lastVal = 0;
            angular.forEach(timestamps, (t: number) => {
                if (points.length <= idx) {
                    points.push({ x: t, y: lastVal });
                }
                else if (points[idx].x > t) {
                    points.splice(idx, 0, { x: t, y: lastVal });
                }
                else {
                    lastVal = points[idx].y;
                }
                idx++;
            });
        }
    }
	//Originally created for the Sekoia service, we may need this functionality in the
	//RA map as well, and potentially other areas. Like the function above, this looked
	//like something we may care to use elsewhere.
	public static CollapsePoints(points: Highcharts.DataPoint[], cumulative: boolean) {
		points.sort((a, b) => {
			return a.x - b.x;
		});
		var lastPoint: Highcharts.DataPoint = null;
		var cumSeriesData: Highcharts.DataPoint[] = [];
		if (points.length == 0)
			return cumSeriesData;
		for (var i = 0; i < points.length; i++) {
			if (lastPoint == null) {
				lastPoint = points[i];
			}
			else {
				if (lastPoint.x === points[i].x) {
					lastPoint.y += points[i].y;
				}
				else {
					cumSeriesData.push(lastPoint);
					if (cumulative) {
						points[i].y += lastPoint.y;
					}
					lastPoint = points[i];
				}
			}
		}
		cumSeriesData.push(lastPoint);
		return cumSeriesData;
	}
	public static SigFig(input, digits) {
		var output;

		if (input == null || angular.isUndefined(input) || isNaN(input)) {
			output = '';
		}
		else if (input == 0) {
			output = 0;
		}
		else {
			if (angular.isUndefined(digits)) {
				digits = 4;
			}
			var roundValue = Math.pow(10, Math.ceil(Math.log(Math.abs(input)) / Math.LN10) - digits);
			//roundValue now holds the power of 10 to which we should round numToFormat to preserve the specified number of significant figures.
			output = Math.round(1000000 * (Math.round((input) / roundValue) * roundValue)) / 1000000;
		}

		return output;
	}

	public static isFloatDecimals(format: string) {
		if (format) { format = format.toLowerCase();  }
		if (format && format.length >= 1 && format != 'radio' && format != 'int' && format != 'float' && (format.charAt(0).toLowerCase() == 'f' || format.charAt(0) == 'p')) {
			var dec = parseInt(format.substring(1));
			if (isNaN(dec)) {
				return 2; //default to 2 decimals if not defined, consistent with other apps
			} else {
				return dec; //returned the assigned decimals
			}
		} else {
			return null;
		}
	}

	public static GetValueFromOptions(att: model.IAssetAttribute, options?: model.IAttributeTypeOption[]) {
		if (options) {
			let op = _.find(options, n => n.attributeOptionTypeID == att.AttributeOptionTypeID);
			let result = op ? op.attributeOptionTypeDesc : '';
			return result;
		}
		return String( att.AttributeOptionTypeID); 
	}


	//This is pulled into its own method because we want it to be used throughout the application anywhere there is an attribute.  We don't want to have to define the functionality everywhere. 
	//It should be noted that there is a similar function in the back end.  This doesn't work exactly the same because this is adding in the engineering units as necessary.  The back end one
	//is mostly concerned with formatting to get stuff into and out of the database. 
	public static AttributeValue(attribute: model.IAssetAttribute, options?: model.IAttributeTypeOption[], excludeUnits?:boolean ) {
		options = options || attribute.Options; 
        excludeUnits = excludeUnits || false; 

		let result: string = attribute.Attribute_string;
		if (attribute.AttributeType) {
			let myDispFormat = (attribute.AttributeType.DisplayFormat || '').toLowerCase();
			var decFormat = this.isFloatDecimals(myDispFormat);
			if (myDispFormat == 'radio') {
				result = this.GetValueFromOptions(attribute, options);
			} else if (myDispFormat === 'boolean') {
				if (_.isNil(attribute.Attribute_int)) {
					result = null;
				}
				else {
					result = attribute.Attribute_int === 1 ? 'true' : 'false';
				}
			} else if (myDispFormat == 'int') {
				if (_.isNil(attribute.Attribute_int)) {
					result = null;
				}
				else {
					result = String(attribute.Attribute_int);
					result = BVUtils.addEngUnits(result, attribute, excludeUnits);
				}
			} else if (myDispFormat == 'float') {
				if (_.isNil(attribute.Attribute_float)) {
					result = null;
				}
				else {
					result = this.SigFig(attribute.Attribute_float, 4);
					result = BVUtils.addEngUnits(result, attribute, excludeUnits);
				}
				
			} else if (myDispFormat == 'date') {
				//We need to turn the date field into a valid date if it is not already one. 
				//Then we need to format the date in the local time zone and culture.  
				if (_.isNil(attribute.Attribute_date)) {
					result = null;
				}
				else {
					let d = BVUtils.GetDate(attribute.Attribute_date);
					if (_.isDate(d)) {
						result = d.toLocaleString();
					}
				}
				
			} else if (!_.isNil(decFormat)) {
				if (_.isNil(attribute.Attribute_float)) {
					result = null;
				}
				else {
					if ((myDispFormat.charAt(0) == 'f')) {
						result = String( attribute.Attribute_float.toFixed(decFormat));
					}
					else if ((myDispFormat.charAt(0) == 'p')) {
						result = String(attribute.Attribute_float.toFixed(decFormat)) + "% ";
					}
					result = BVUtils.addEngUnits(result, attribute, excludeUnits);
				}
			}
		}

		return result;
	}

	private static addEngUnits(val: string, attribute: model.IAssetAttribute, excludeUnits?: boolean) {
		if (!excludeUnits && attribute.AttributeType) {
			return val + " " + (attribute.AttributeType.EngrUnits || "");
		}
		return val; 
	}

	public static SetAttributeValue(attribute: model.IAssetAttribute, value: string) {
		if (!attribute.AttributeType) {
			attribute.Attribute_string = value;
		} else {
			var format = (attribute.AttributeType.DisplayFormat || 'string').toLowerCase();
			if (format == 'string' || format == 'curve') {
				attribute.Attribute_string = value;
			} else if (format == 'float' || (format.length >= 1 && (format.charAt(0) == 'f' || format.charAt(0) == 'p'))) {
				if (!value) {
					attribute.Attribute_float = null;
				} else {
					attribute.Attribute_float = parseFloat(value);
				}
				if (isNaN(attribute.Attribute_float)) {
					attribute.Attribute_float = 0;
				}
			} else if (format == 'int') {
				attribute.Attribute_int = parseInt(value);
				if (isNaN(attribute.Attribute_int)) {
					attribute.Attribute_int = 0;
				}
			} else if (format == 'radio') {
				let opt = _.find(attribute.Options, n => {
					return n.attributeOptionTypeDesc === value;
				});
				attribute.AttributeOptionTypeID = opt ? opt.attributeOptionTypeID : attribute.AttributeOptionTypeID;
			} else if (format === 'boolean') {
				attribute.Attribute_int = value === 'true' ? 1 : 0;
			} else if (format === 'date') {
				attribute.Attribute_date = BVUtils.GetDate(value);
			}
		}
	}


}


// WEBPACK FOOTER //
// ./Common/BVUtils.ts